<script setup lang="ts">
defineOptions({
  name: "login"
});

import { computed, onMounted, reactive, ref } from "vue";
import type { Component } from "vue";
import { $t } from "@/locales";
import { useAppStore } from "@/store/modules/app";
import { useThemeStore } from "@/store/modules/theme";
import { loginModuleRecord } from "@/constants/app";
import PwdLogin from "./modules/pwd-login.vue";
import ResetPwd from "./modules/reset-pwd.vue";
import { useAuthStore } from "@/store/modules/auth";
import { ossUrl } from "@/constants/apiConfig";

interface Props {
  /** The login module */
  module?: UnionKey.LoginModule;
}

const props = defineProps<Props>();
const { getSystemInfo, systemInfo } = useAuthStore();
const appStore = useAppStore();
const themeStore = useThemeStore();

interface LoginModule {
  label: string;
  component: Component;
}

const moduleMap: Record<UnionKey.LoginModule, LoginModule> = {
  "pwd-login": { label: loginModuleRecord["pwd-login"], component: PwdLogin },
  "reset-pwd": { label: loginModuleRecord["reset-pwd"], component: ResetPwd },
};

const activeModule = computed(() => moduleMap[props.module || "pwd-login"]);

const videoPlayer = ref<HTMLVideoElement | null>(null);

/**
 * 获取系统名称，logo等信息
 * */
onMounted(async () => {
  await getSystemInfo();
});
</script>

<template>
  <div>
    <NScrollbar>
      <div class="login-page">
        <div class="left-item">
          <video ref="videoPlayer" class="h-full" muted autoplay loop fit="fill" :controls="false">
            <source src="@/assets/imgs/login.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <div class="mask-white"></div>

          <!-- <NImage v-if="systemInfo.logo" width="180" :src="ossUrl + systemInfo.logo" preview-disabled
            class="title-icon top-40px" /> -->
          <SvgIcon local-icon="gvs" class="title-icon text-180px top-[-30px]" />

          <div class="bottom-title-box">
            <p class="text-2xl text-lightGrey leading-56px">
              {{ $t("system.firstDesc") }}
            </p>
            <p class="text-4xl text-deepGrey font-semibold leading-56px">
              {{ $t("system.sedDesc") }}
            </p>
          </div>
        </div>
        <div class="right-item p-4 flex-col">
          <header>
            <NSpace justify="end">
              <NButton quaternary type="primary">
                <SvgIcon local-icon="helpDoc" class="text-2xl" />
              </NButton>

              <LangSwitch :lang="appStore.locale" :lang-options="appStore.localeOptions" :show-tooltip="false"
                @change-lang="appStore.changeLocale" />
            </NSpace>
          </header>

          <main class="flex-center">
            <div class="h-200 w-2/3 bg-white">
              <h3 class="mt-120px text-4xl text-deepBlack font-bold leading-80px">
                {{ $t(activeModule.label) }}
              </h3>
              <p class="text-2xl text-lightGrey">
                {{ $t("system.title") }}
                <!-- {{
                  systemInfo.systemName
                    ? systemInfo.systemName
                    : $t("system.title")
                }} -->
              </p>
              <div class="pt-60px">
                <Transition :name="themeStore.page.animateMode" mode="out-in" appear>
                  <component :is="activeModule.component" />
                </Transition>
              </div>
            </div>
          </main>
          <footer class="flex-1 relative">
            <div class="absolute bottom-0 left-1/2 -translate-x-1/2">
              <p class="text-lightGrey">
                粤ICP备08008610号
                <!-- {{
                  systemInfo.recordNumber
                    ? systemInfo.recordNumber
                    : "粤ICP备08008610号"
                }} -->
              </p>
            </div>
          </footer>
        </div>
      </div>
    </NScrollbar>
  </div>
</template>

<style scoped>
.login-page {
  display: grid;
  grid-template-columns: 6fr minmax(450px, 4fr);
  height: 100vh;
  /* overflow: auto; */
}

.right-item {
  background-color: #ffffff;
}

.left-item {
  background-color: #f1f3ff;
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
}

.mask-white {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-image: radial-gradient(ellipse,
      #00000000,
      #00000000,
      #f1f3ff,
      #ffffff);
}

.title-icon {
  position: absolute;
  left: 40px;
  z-index: 9999999999999999;
}

.bottom-title-box {
  position: absolute;
  bottom: 40px;
  left: 40px;
  z-index: 999999999;
}

.bottom-code {
  position: absolute;
  bottom: 40px;
}

@media (max-width: 750px) {
  .bottom-title-box {
    display: none;
  }
}
</style>
